import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import {
  showAlert,
  showErrorAlert,
  showAlertDescongelar,
  showAlertEstadoConDescongelar,
  showAlertSelectCongelacion,
} from "../../../components/molecules/ShowAlert";
import { EstudianteDescongelarList } from "../../../modules/estudiante/EstudianteDescongelarList";
import {
  getAllRegistros,
  actualizarRegistroCongelado,
} from "../../../services/registro.service";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getAllInscripciones } from "../../../services/inscripcion.service";
import { getOneTipoAdmision } from "../../../services/tipoAdmision.service";

const esCongelado = (reg = {}) => {
  if (reg.estado?.trim().toLowerCase() === "congelado") return true;
  return !!reg.estaCongelado;
};

export const DescongelarEstudiantesPage = () => {
  const [registros, setRegistros] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const navigate = useNavigate();
  const [freezeTypes, setFreezeTypes] = useState({});

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [regs, ests, inscs] = await Promise.all([
        getAllRegistros(),
        getAllEstudiantes(),
        getAllInscripciones(),
      ]);

      const dicEst = {};
      for (const e of ests) {
        const idE = e.estudiante?.idEstudiante?.trim();
        if (idE) {
          dicEst[idE] = e;
        }
      }

      const fechaRegistroActual = new Date().toLocaleDateString("es-BO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      // ✅ OPTIMIZACIÓN: Crear caché para evitar peticiones duplicadas
      const cacheTipoAdmision = {};
      const obtenerTipoAdmisionConCache = async (idTipoAdmision) => {
        if (!idTipoAdmision) return "N/A";
        
        // Si ya está en caché, retornar inmediatamente
        if (cacheTipoAdmision[idTipoAdmision]) {
          return cacheTipoAdmision[idTipoAdmision];
        }
        
        // Si no está, hacer la petición y guardar en caché
        try {
          const tipoInfo = await getOneTipoAdmision(idTipoAdmision);
          const nombre = tipoInfo?.nombre || "N/A";
          cacheTipoAdmision[idTipoAdmision] = nombre;
          return nombre;
        } catch (err) {
          console.error("Error obteniendo Tipo de Admisión:", err);
          cacheTipoAdmision[idTipoAdmision] = "N/A";
          return "N/A";
        }
      };

      // ✅ Obtener IDs únicos de tipo de admisión
      const idsUnicos = [...new Set(
        regs
          .map(r => dicEst[r.idEstudiante?.trim()]?.estudiante?.idTipoAdmision)
          .filter(Boolean)
      )];

      // ✅ Cargar todos los tipos de admisión PRIMERO (en paralelo pero limitado)
      console.log(`📦 Cargando ${idsUnicos.length} tipos de admisión únicos...`);
      await Promise.all(idsUnicos.map(id => obtenerTipoAdmisionConCache(id)));

      // ✅ Ahora transformar los datos (usando el caché, sin nuevas peticiones)
      const dataTransformada = await Promise.all(
        regs.map(async (r) => {
          const idEst = r.idEstudiante?.trim();
          const estEncontrado = dicEst[idEst] || {};
          const persona = estEncontrado.persona || {};
          const congelado = esCongelado(r);

          const nombreCompleto = `${persona.nombre || "SinNombre"} ${
            persona.apellido || ""
          }`.trim();
          
          // Usar el caché (no hace peticiones HTTP)
          const tipoAdmisionNombre = await obtenerTipoAdmisionConCache(
            estEncontrado.estudiante?.idTipoAdmision
          );

          return {
            idRegistro: r.idRegistro,
            estado: r.estado || "N/A",
            estaCongelado: congelado,
            tipoCongelacion: congelado ? r.tipoCongelacion || "N/A" : "N/A",
            fechaRegistro: fechaRegistroActual,
            estudiante: {
              idEstudiante: idEst,
              nombre: persona.nombre || "SinNombre",
              apellido: persona.apellido || "",
              nombreCompleto,
              tipoAdmi: tipoAdmisionNombre,
            },
          };
        })
      );
      const filtrados = [];
      const nombresUnicos = new Set();

      for (const reg of dataTransformada) {
        const key = reg.estudiante.nombreCompleto.toLowerCase();
        if (!nombresUnicos.has(key)) {
          nombresUnicos.add(key);
          filtrados.push(reg);
        }
      }

      console.log(`✅ Datos cargados: ${filtrados.length} estudiantes`);
      setRegistros(filtrados);
    } catch (error) {
      console.error("Error al cargar registros:", error);
      showErrorAlert({ text: "Error al cargar los registros" });
    }
  };

  const handleActualizarEstado = async (idRegistro, congelar, tipo) => {
    try {
      const body = {
        estaCongelado: !!congelar,
        tipoCongelacion: congelar ? tipo : null,
      };
      await actualizarRegistroCongelado(idRegistro, body);
      showAlertEstadoConDescongelar({
        text: congelar
          ? "Estudiante congelado con éxito."
          : "Estudiante descongelado.",
        icon: "success",
      });
      cargarDatos();
    } catch (err) {
      console.error("❌ Error al actualizar estado:", err);
      showErrorAlert({ text: "No se pudo actualizar el estado." });
    }
  };

  const registrosFiltrados = registros.filter((reg) => {
    const texto = busqueda.toLowerCase();
  
    const nombreCompleto = `${reg.estudiante.nombre} ${reg.estudiante.apellido}`.toLowerCase();
    const estado = reg.estado?.toLowerCase() || "";
    const tipoAdmision = reg.estudiante?.tipoAdmi?.toLowerCase() || "";
    const tipoCongelacion = reg.tipoCongelacion?.toLowerCase() || "";
  
    return (
      nombreCompleto.includes(texto) ||
      estado.includes(texto) ||
      tipoAdmision.includes(texto) ||
      tipoCongelacion.includes(texto)
    );
  });
  

  return (
    <ContenedorPageTemplate titulo="Congelar / Descongelar Estudiantes">
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre, estado, tipo de admisión o tipo de congelación"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 flex-1"
        />
      </div>

      <EstudianteDescongelarList
        registros={registrosFiltrados}
        onActualizarEstado={handleActualizarEstado}
        freezeTypes={freezeTypes}
        setFreezeTypes={setFreezeTypes}
        onAsignarModulo={(reg) => {
          const tipo = reg.tipoCongelacion?.trim().toLowerCase();
          if (tipo === "voluntario" || tipo === "institucional") {
            showAlertDescongelar({
              icon: "warning",
              text: `Para asignar un módulo al estudiante ${reg.estudiante.nombre} ${reg.estudiante.apellido}, primero debes descongelarlo.`,
            });
            return;
          }

          navigate(
            `/asignar-modulo-Descongelar/${reg.estudiante.idEstudiante}`,
            {
              state: { estudiante: reg.estudiante },
            }
          );
        }}
      />
    </ContenedorPageTemplate>
  );
};

export default DescongelarEstudiantesPage;
